import { lazy, type ComponentType } from "react";

// Stale-chunk recovery for code-split routes.
//
// Each deploy re-hashes chunk filenames (`Create-ABC123.js` → `Create-XYZ789.js`).
// A client still running the previous `index.html` references the old names, so
// navigating to a lazy route rejects with "Failed to fetch dynamically imported
// module". Vercel serves `index.html` revalidated (only `/assets/*` are
// immutable-cached), so a full reload pulls the fresh HTML + new chunk names and
// the navigation succeeds.
const RELOAD_FLAG = "chunk-reload-attempted";

// Reload the page once to pick up a freshly-deployed build. Guarded by
// sessionStorage so a genuinely-missing chunk can't trigger an infinite loop.
// Returns true if a reload was kicked off, false if one was already attempted.
export function triggerReloadOnce(): boolean {
  if (window.sessionStorage.getItem(RELOAD_FLAG)) return false;
  window.sessionStorage.setItem(RELOAD_FLAG, "1");
  window.location.reload();
  return true;
}

// `React.lazy` wrapper that recovers from stale-chunk import failures by reloading
// once. On success it clears the guard so a future deploy gets a fresh reload
// budget; a second consecutive failure is a real error and is re-thrown into the
// nearest error boundary instead of looping.
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      window.sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (error) {
      if (triggerReloadOnce()) {
        // Halt rendering until the reload takes over — never resolves, so the
        // Suspense fallback stays up instead of flashing a broken screen.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}
